import { Worksheet } from '../../types/worksheet';
import { Modal, Checkbox, ButtonV2, TooltipV2 } from '@cimplico/components';
import { useCarryForward } from '../../queries/use-carry-forward';
import BulkCarryForwardResourcesRow from './BulkCarryForwardResourcesRow';
import { useState, useCallback, useMemo } from 'react';
import { ResourceType } from '../../types/carry-forward';
import { toastNotification } from '../../components/toast/Toast';
import BulkCarryForwardResourcesErrors, { ErrorItem } from './BulkCarryForwardResourcesErrors';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import queryClient from '../../query-client';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';

type BulkCarryForwardResourcesConfirmProps = {
    selectedWorksheets: Worksheet[];
    priorWorkpaperId: string;
    onClose: () => void;
    onCarryForward: () => void;
};

function BulkCarryForwardResourcesConfirm({
    selectedWorksheets,
    priorWorkpaperId,
    onClose,
    onCarryForward,
}: BulkCarryForwardResourcesConfirmProps): JSX.Element {
    const { trackEvent } = useIntercom();
    // Retrieves workpaper ID from context and initializes carry forward mutation hook
    const { id: workpaperId } = useWorkpaperContext();
    const carryForwardQuery = useCarryForward();

    // Tracks which resource types are selected for carry forward and stores entry IDs by worksheet
    const [resourceSelections, setResourceSelections] = useState<{ entries: boolean }>({ entries: true });
    const [entriesMap, setEntriesMap] = useState<Record<string, string[]>>({});

    // Stores results of carry forward operation including success count and any errors
    const [carryForwardResults, setCarryForwardResults] = useState<{ successCount: number; errors: ErrorItem[] } | null>(null);

    // Callback to update entries map when entries are retrieved and returned from BulkCarryForwardResourcesRow components
    const handleGetEntries = useCallback(
        (worksheetId: string, ids: string[]) => setEntriesMap((prev) => ({ ...prev, [worksheetId]: ids })),
        []
    );

    // Filters worksheets to only those with prior worksheet IDs and tracks loading state.
    const rows = useMemo(() => selectedWorksheets.filter((sw) => sw.priorWorksheetId), [selectedWorksheets]);
    const allRowsLoaded = rows.length === Object.keys(entriesMap).length;
    const totalEligibleEntries = useMemo(() => Object.values(entriesMap).flat().length, [entriesMap]);
    const hasEligibleSelectedResource = resourceSelections.entries && totalEligibleEntries > 0;

    // Submit enabled only when eligible resources are selected and all rows are loaded
    const canSubmit = hasEligibleSelectedResource && allRowsLoaded;

    function onSubmit(): void {
        if (!resourceSelections.entries) return;
        const allEntryIds = rows.flatMap((sw) => entriesMap[sw.id] || []);
        if (allEntryIds.length === 0) return;

        carryForwardQuery.mutate(
            {
                priorWorkpaperId,
                currentWorkpaperId: workpaperId,
                payloads: allEntryIds.map((eid) => ({ type: ResourceType.ENTRY, priorResourceId: eid })),
            },
            {
                onSuccess: (res) => {
                    const successes = res.filter((r) => r.outcome === 'success');
                    const errors = res.filter((r) => r.outcome === 'error');
                    if (errors.length > 0) {
                        const items: ErrorItem[] = errors.map((r) => {
                            const ws = rows.find((sw) => (entriesMap[sw.id] || []).includes(r.priorResourceId));
                            return {
                                worksheetName: ws!.name,
                                resourceName: r.metadata.resourceName!,
                                message: r.metadata.outcomeMessage ?? 'Carrying forward resource failed',
                            };
                        });
                        setCarryForwardResults({ successCount: successes.length, errors: items });
                        return;
                    }
                    // If response includes success results then trigger intercom event to record them
                    // Add additional resource types as they are implemented.
                    const successCounts = successes.reduce(
                        (acc, { resourceType }) => {
                            acc.total++;
                            if (resourceType === ResourceType.ENTRY) acc.entries++;
                            return acc;
                        },
                        { total: 0, entries: 0 }
                    );
                    if (successCounts.total > 0) {
                        trackEvent(
                            intercomEvents.carryForward.bulkCarryForwardResources.event,
                            intercomEvents.carryForward.bulkCarryForwardResources.metaData(
                                workpaperId,
                                priorWorkpaperId,
                                successCounts.entries
                            )
                        );
                    }
                    onCarryForward();
                    toastNotification('success', `Successfully carried forward ${successes.length} resources`);
                },
                onError: () => {
                    onClose();
                    toastNotification('error', `Failed to carry forward resources. Please try again.`);
                },
            }
        );
    }

    return (
        <Modal isOpen onClose={onClose}>
            <div className="py-8 px-12 min-w-[800px] min-h-[500px] max-h-[775px] flex flex-col">
                <div className="overflow-auto grow px-1">
                    <div className="flex items-start justify-between">
                        <hgroup className={'text-left'}>
                            <h2 className="text-lg text-gray-900 font-medium">Carry Forward Resources</h2>
                            <p className="text-sm text-gray-600 max-w-[100ch]">
                                Select which resources you would like to carry forward. Resources that have already been carried forward,
                                and linked entries will not be included. When you click confirm, eligible selected resources will be carried
                                forward from worksheets in the prior workpaper that are linked to the selected worksheets.
                            </p>
                        </hgroup>
                    </div>
                    {carryForwardResults && (
                        <BulkCarryForwardResourcesErrors results={carryForwardResults} onCloseBanner={() => setCarryForwardResults(null)} />
                    )}
                    <hr className="my-4" />
                    <div className={'flex flex-col gap-2 max-w-[1016px]'}>
                        <div className="flex flex-col items-start justify-start space-y-2 pt-2 pb-4">
                            <TooltipV2
                                disabled={allRowsLoaded && totalEligibleEntries > 0}
                                message={'No eligible entries to carry forward on selected worksheets'}
                            >
                                <Checkbox
                                    label={'Carry forward entries'}
                                    checked={resourceSelections.entries && totalEligibleEntries > 0}
                                    onChange={() => setResourceSelections((prev) => ({ ...prev, entries: !prev.entries }))}
                                    disabled={allRowsLoaded && totalEligibleEntries === 0}
                                />
                            </TooltipV2>
                        </div>
                        <div className="relative w-full h-auto">
                            <div className="grid grid-cols-2 gap-x-8 border rounded-md w-full">
                                <div className="col-span-full grid grid-cols-2 gap-x-6 border-b last:border-0 items-center py-2 text-sm font-medium text-gray-800 bg-gray-100 rounded-t px-4">
                                    <p className="text-sm justify-self-start">
                                        Selected Worksheet {allRowsLoaded ? `(${selectedWorksheets.length})` : ''}
                                    </p>
                                    <p className="text-sm justify-self-start">
                                        Eligible Prior Entries {allRowsLoaded ? `(${totalEligibleEntries})` : ''}
                                    </p>
                                </div>

                                {rows.map((sw) => (
                                    <BulkCarryForwardResourcesRow
                                        worksheet={sw}
                                        priorWorkpaperId={priorWorkpaperId}
                                        onGetEntries={handleGetEntries}
                                        key={sw.id}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="border-t pt-4 mt-4 flex justify-end gap-2">
                    <ButtonV2 onClick={onClose} label="Cancel" size="sm" type="secondary" isDisabled={carryForwardQuery.isLoading} />
                    <ButtonV2
                        onClick={onSubmit}
                        label="Confirm"
                        size="sm"
                        type="primary"
                        isDisabled={!canSubmit || carryForwardQuery.isLoading}
                        isLoading={carryForwardQuery.isLoading}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default BulkCarryForwardResourcesConfirm;
